/**
 * AdminNewsCuratorBatchPage — Module "Curator IA — Actualités sportives".
 *
 * Permet à l'administratrice de coller un bloc JSON préparé en amont par
 * ChatGPT (ou tout autre outil éditorial), de l'importer en un clic et de
 * voir immédiatement :
 *   - les articles créés, dédoublonnés, en erreur ;
 *   - les articles effectivement mis à la Une ;
 *   - l'historique des lots précédents avec possibilité d'annulation.
 *
 * Ce module n'exécute pas de recherche automatique vers ChatGPT ; il part
 * d'un bloc fourni manuellement. Le chargement d'un bloc par l'admin vaut
 * validation éditoriale du lot.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Download, Upload, Loader2, CheckCircle2, XCircle, GitMerge,
  Clock, FileJson, ShieldCheck, Undo2, Star, Globe, ExternalLink,
} from "lucide-react";
import { showError } from "@/lib/errors";

const EMPTY_TEMPLATE = {
  format: "aito-news-curator-v1",
  generated_at: new Date().toISOString(),
  items: [
    { url: "https://www.exemple.pf/article-1", priority: 1, reason: "Pourquoi cet article" },
    { url: "https://www.exemple.pf/article-2", priority: 2 },
  ],
};

const STATUS_META = {
  created: { label: "Créé", icon: CheckCircle2, cls: "text-emerald-600" },
  deduped: { label: "Déjà présent", icon: GitMerge, cls: "text-sky-600" },
  error: { label: "Erreur", icon: XCircle, cls: "text-red-600" },
};

const ERROR_LABELS = {
  url_malformed: "URL mal formée",
  scheme_not_http: "URL non HTTP(S)",
  no_hostname: "Hôte manquant",
  dns_failed: "Nom de domaine introuvable",
  private_ipv4: "Adresse privée refusée",
  private_ipv6: "Adresse privée refusée",
  timeout: "Délai dépassé",
  network_error: "Erreur réseau",
  paywall_or_auth: "Article payant ou authentifié",
  not_html: "Contenu non HTML",
  unexpected_error: "Erreur inattendue",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ResultsTable({ summary, results }) {
  if (!results?.length) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="curator-batch-results">
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge className="bg-emerald-600 text-white">Créés : {summary.created}</Badge>
        <Badge className="bg-sky-600 text-white">Dédupliqués : {summary.deduped}</Badge>
        <Badge className="bg-red-600 text-white">Erreurs : {summary.errored}</Badge>
        <Badge variant="outline">
          <Star className="h-3 w-3 mr-1" /> Promus à la Une : {summary.featured_promoted}
        </Badge>
        <Badge variant="outline">
          Retirés : {summary.featured_demoted}
        </Badge>
        {summary.featured_locked_kept > 0 && (
          <Badge variant="outline" className="border-amber-400 text-amber-700">
            🔒 Verrouillés conservés : {summary.featured_locked_kept}
          </Badge>
        )}
      </div>
      <div className="space-y-1.5 max-h-96 overflow-y-auto rounded-md border p-2 bg-background/60">
        {results.map((r, i) => {
          const meta = STATUS_META[r.status] || STATUS_META.error;
          const Icon = meta.icon;
          return (
              <div className="flex items-start gap-2 text-sm py-1.5" data-testid={`curator-result-${i}`}>
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.cls}`} />
                {r.image_url && (
                  <img
                    src={r.image_url}
                    alt=""
                    loading="lazy"
                    className="h-10 w-14 object-cover rounded border shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    {r.title || r.input_url}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono truncate">
                    {r.source_name ? `${r.source_name} · ` : ""}priorité {r.priority}
                    {r.status === "error" && ` · ${ERROR_LABELS[r.error] || r.error}`}
                    {r.image_url && r.status !== "error" ? " · 🖼️" : ""}
                  </div>
                </div>
                <a href={r.input_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
          );
        })}
      </div>
    </div>
  );
}

function BatchRow({ b, onRollback, onOpen }) {
  const nb = (status) => (b.results || []).filter((r) => r.status === status).length;
  const isActive = b.status === "active";
  return (
    <div className="p-3 rounded-md border bg-background hover:bg-muted/40 transition" data-testid={`curator-batch-row-${b.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground font-mono">
            {b.id.slice(0, 8)} · {formatDate(b.created_at)}
          </div>
          <div className="flex flex-wrap gap-1 mt-1 text-xs">
            <Badge variant={isActive ? "default" : "outline"} className={isActive ? "bg-emerald-600 text-white" : ""}>
              {isActive ? "Actif" : "Annulé"}
            </Badge>
            <span className="text-muted-foreground">
              {b.items_count} article(s) · ✓ {nb("created")} / ↻ {nb("deduped")} / ✗ {nb("error")}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => onOpen(b)}
            data-testid={`curator-batch-open-${b.id}`}
          >
            Détails
          </Button>
          {isActive && (
            <Button
              size="sm" variant="outline" className="text-red-600 border-red-200"
              onClick={() => onRollback(b)}
              data-testid={`curator-batch-rollback-${b.id}`}
            >
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Annuler le lot
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminNewsCuratorBatchPage() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [raw, setRaw] = useState("");
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [openedBatch, setOpenedBatch] = useState(null);
  const fileInputRef = useRef(null);

  // Redirection si non-admin (après chargement complet de l'auth).
  // L'app utilise `user.roles` (array), pas `user.role`.
  const isModerator = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));
  useEffect(() => {
    if (!authLoading && user && !isModerator) {
      nav("/", { replace: true });
    }
  }, [authLoading, user, isModerator, nav]);

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const { data } = await api.get("/admin/news-curator/batches?limit=20");
      setBatches(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Historique :" });
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, []);

  const handleFile = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result || ""));
    reader.onerror = () => toast.error("Impossible de lire le fichier.");
    reader.readAsText(file);
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/news-curator/template");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "aito-news-curator-template.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showError(e, { prefix: "Téléchargement :" });
    }
  };

  const insertSample = () => {
    setRaw(JSON.stringify(EMPTY_TEMPLATE, null, 2));
  };

  const importBatch = async () => {
    if (!raw.trim()) {
      toast.error("Colle d'abord un bloc JSON ou charge un fichier.");
      return;
    }
    setRunning(true);
    setLastResult(null);
    try {
      // Permet aux admins d'envoyer soit un objet JSON soit une chaîne brute :
      // on tente le parse côté client pour afficher les erreurs rapidement,
      // puis on envoie l'objet au backend (qui re-valide de toute façon).
      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (e) {
        toast.error(`JSON invalide : ${e.message}`);
        setRunning(false);
        return;
      }
      const { data } = await api.post("/admin/news-curator/import", payload);
      setLastResult(data);
      toast.success(
        `Import terminé : ${data.summary.created} créé(s), ${data.summary.deduped} déjà présent(s), ${data.summary.errored} erreur(s).`
      );
      loadBatches();
    } catch (e) {
      showError(e, { prefix: "Import :" });
    } finally {
      setRunning(false);
    }
  };

  const rollbackBatch = async (batch) => {
    if (!window.confirm(
      `Annuler le lot du ${formatDate(batch.created_at)} ? Les articles créés par ce lot seront retirés de la publication. Les articles existants ne seront pas supprimés.`
    )) return;
    try {
      await api.post(`/admin/news-curator/batches/${batch.id}/rollback`);
      toast.success("Lot annulé. Les articles créés ont été retirés.");
      loadBatches();
    } catch (e) {
      showError(e, { prefix: "Annulation :" });
    }
  };

  const openBatch = async (batch) => {
    try {
      const { data } = await api.get(`/admin/news-curator/batches/${batch.id}`);
      setOpenedBatch(data);
    } catch (e) {
      showError(e, { prefix: "Ouverture :" });
    }
  };

  // Écran d'attente pendant le chargement de la session, pour éviter
  // d'afficher la page avant que `user` ne soit disponible.
  if (authLoading || !user) {
    return (
      <div className="container max-w-6xl py-6 space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (!isModerator) {
    return (
      <div className="container max-w-6xl py-10">
        <Card className="p-6"><p className="text-sm">Accès réservé à l'administration autorisée.</p></Card>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-6 space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-[hsl(var(--aito-lagoon))]" />
          <h1 className="font-display text-2xl font-semibold">Curator IA — Actualités sportives</h1>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl leading-relaxed">
          Colle le bloc JSON préparé par ChatGPT (ou tout outil éditorial) et importe-le en un clic.
          Les articles créés deviennent les actualités visibles sur AITO.PF et la Une se renouvelle
          selon l'ordre de priorité du bloc. Les articles verrouillés par <strong>📌 épingle</strong>
          sont préservés. <strong>Le chargement d'un bloc vaut validation éditoriale.</strong>
        </p>
        <p className="text-[11px] text-muted-foreground">
          <Link to="/admin" className="underline">← Retour admin</Link>
          &nbsp;·&nbsp; La recherche quotidienne ChatGPT n'est pas automatisée côté serveur :
          ce module traite les blocs que tu lui soumets.
        </p>
      </header>

      {/* Zone principale : saisie + import */}
      <Card className="p-5 space-y-4" data-testid="curator-batch-input-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display font-semibold text-lg">1. Colle ton bloc d'actualités</h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="curator-template-btn">
              <Download className="h-4 w-4 mr-1.5" /> Modèle JSON
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="curator-file-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Charger un fichier
            </Button>
            <input
              ref={fileInputRef} type="file" accept=".json,application/json"
              onChange={handleFile} className="hidden"
            />
            <Button variant="ghost" size="sm" onClick={insertSample} data-testid="curator-sample-btn">
              Exemple
            </Button>
          </div>
        </div>
        <Textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={14}
          className="font-mono text-xs"
          placeholder={`Colle ici le bloc JSON au format aito-news-curator-v1.\nFormat :\n{\n  "format": "aito-news-curator-v1",\n  "generated_at": "2026-10-30T07:00:00+10:00",\n  "items": [\n    {\n      "url": "https://...",\n      "priority": 1,\n      "reason": "...",\n      "image_url": "https://.../photo.jpg"\n    }\n  ]\n}\n\nLe champ "image_url" est optionnel : si absent, l'image Open Graph de la source sera utilisée en secours.`}
          data-testid="curator-batch-textarea"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground max-w-xl">
            <strong>Confiance éditoriale par défaut :</strong> les articles valides sont publiés automatiquement.
            Les URLs inaccessibles, mal formées ou pointant vers une ressource interne sont écartées en silence.
            Les photographies sources ne sont jamais reprises automatiquement.
          </p>
          <Button
            onClick={importBatch}
            disabled={running || !raw.trim()}
            className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
            data-testid="curator-batch-import-btn"
          >
            {running ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Import en cours…</> :
              <><FileJson className="h-4 w-4 mr-1.5" /> Importer et actualiser la Une</>}
          </Button>
        </div>
        {lastResult && (
          <>
            <Separator />
            <div>
              <h3 className="font-display font-semibold text-sm mb-1">2. Résultat de l'import</h3>
              <p className="text-[11px] text-muted-foreground">
                Lot <code className="font-mono">{lastResult.batch_id.slice(0, 8)}</code> enregistré dans l'historique.
              </p>
              <ResultsTable summary={lastResult.summary} results={lastResult.results} />
            </div>
          </>
        )}
      </Card>

      {/* Historique */}
      <Card className="p-5 space-y-3" data-testid="curator-batch-history-card">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Historique des imports</h2>
          <Button variant="ghost" size="sm" onClick={loadBatches}>
            <Clock className="h-4 w-4 mr-1.5" /> Rafraîchir
          </Button>
        </div>
        {loadingBatches ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : batches.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun lot importé pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <BatchRow key={b.id} b={b} onRollback={rollbackBatch} onOpen={openBatch} />
            ))}
          </div>
        )}
      </Card>

      {/* Détail d'un lot */}
      {openedBatch && (
        <Card className="p-5 space-y-3" data-testid="curator-batch-detail-card">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display font-semibold text-lg">
                Détail du lot <code className="font-mono text-sm">{openedBatch.id.slice(0, 8)}</code>
              </h2>
              <p className="text-xs text-muted-foreground">
                {formatDate(openedBatch.created_at)} ·
                généré le {openedBatch.generated_at ? formatDate(openedBatch.generated_at) : "—"} ·
                statut : <strong>{openedBatch.status}</strong>
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setOpenedBatch(null)}>
              Fermer
            </Button>
          </div>
          {openedBatch.results && (
            <ResultsTable
              summary={{
                created: openedBatch.results.filter((r) => r.status === "created").length,
                deduped: openedBatch.results.filter((r) => r.status === "deduped").length,
                errored: openedBatch.results.filter((r) => r.status === "error").length,
                featured_promoted: openedBatch.featured_update_summary?.promoted ?? 0,
                featured_demoted: openedBatch.featured_update_summary?.demoted ?? 0,
                featured_locked_kept: openedBatch.featured_update_summary?.locked_kept ?? 0,
              }}
              results={openedBatch.results}
            />
          )}
        </Card>
      )}
    </div>
  );
}
